import {useState, useCallback} from "react";
import {syncPush, syncPull} from "../pages/generic/crud";
import {showToast, TOAST_SUCCESS, TOAST_FAILURE} from "../utils/toast/Toast";

/**
 * Custom hook for handling sync operations (push/pull)
 *
 * @param {number} entityId - The entity type ID for sync
 * @param {function} onComplete - Callback after sync completes (for data refresh)
 * @param {object} options - Optional configuration
 * @param {boolean} options.pushEnabled - Whether push is enabled (default: true)
 * @param {boolean} options.pullEnabled - Whether pull is enabled (default: true)
 * @returns {object} - Sync state and functions
 */
export function useSyncOperations(entityId, onComplete, options = {}) {
    const { pushEnabled = true, pullEnabled = true } = options;

    const [showDialog, setShowDialog] = useState(false);
    const [syncType, setSyncType] = useState(null);
    const [isInProgress, setIsInProgress] = useState(false);
    const [syncResult, setSyncResult] = useState(null);

    const pushToServer = useCallback(async () => {
        if (!pushEnabled) {
            showToast("Push is not enabled for this entity", TOAST_FAILURE);
            return;
        }

        setSyncType('push');
        setSyncResult(null);
        setIsInProgress(true);
        setShowDialog(true);

        try {
            const response = await syncPush(entityId);
            const result = response.data;
            setSyncResult(result);

            if (result.success) {
                showToast(`Push successful! ${result.successCount} records synced.`, TOAST_SUCCESS);
            } else {
                showToast(`Push failed: ${result.message}`, TOAST_FAILURE);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
            setSyncResult({
                success: false,
                message: `Push failed: ${errorMsg}`,
                totalProcessed: 0,
                successCount: 0,
                failedCount: 0
            });
            showToast(`Push failed: ${errorMsg}`, TOAST_FAILURE);
        } finally {
            setIsInProgress(false);
            if (onComplete) {
                onComplete();
            }
        }
    }, [entityId, onComplete, pushEnabled]);

    const pullFromServer = useCallback(async () => {
        if (!pullEnabled) {
            showToast("Pull is not enabled for this entity", TOAST_FAILURE);
            return;
        }

        setSyncType('pull');
        setSyncResult(null);
        setIsInProgress(true);
        setShowDialog(true);

        try {
            const response = await syncPull(entityId);
            const result = response.data;
            setSyncResult(result);

            if (result.success) {
                const counts = `Inserted: ${result.insertedCount || 0}, Updated: ${result.updatedCount || 0}, Deleted: ${result.deletedCount || 0}`;
                showToast(`Pull successful! ${counts}`, TOAST_SUCCESS);
            } else {
                showToast(`Pull failed: ${result.message}`, TOAST_FAILURE);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
            setSyncResult({
                success: false,
                message: `Pull failed: ${errorMsg}`,
                totalCount: 0,
                insertedCount: 0,
                updatedCount: 0,
                deletedCount: 0
            });
            showToast(`Pull failed: ${errorMsg}`, TOAST_FAILURE);
        } finally {
            setIsInProgress(false);
            if (onComplete) {
                onComplete();
            }
        }
    }, [entityId, onComplete, pullEnabled]);

    const closeDialog = useCallback(() => {
        if (!isInProgress) {
            setShowDialog(false);
            setSyncResult(null);
            setSyncType(null);
        }
    }, [isInProgress]);

    return {
        showDialog,
        syncType,
        isInProgress,
        syncResult,
        pushToServer,
        pullFromServer,
        closeDialog,
        // Expose capabilities for UI conditional rendering
        pushEnabled,
        pullEnabled
    };
}
